import { AfterViewInit, Component, DestroyRef, ElementRef, inject, Input, OnChanges, OnInit, SimpleChanges, ViewChild, } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { Store } from '@ngrx/store';
import { cloneDeep } from 'lodash';

import { environment } from '@env/environment';
import { DocumentService } from '@core/api/document.service';
import { selectActAsUser } from '@store/auth/auth.selectors';
import * as fromApp from '@store/index';
import { setActiveSite } from '@store/site/site.actions';
import { DialogUploadDocComponent } from '@shared/dialogs/dialog-upload-doc/dialog-upload-doc.component';
import { DialogYesNoComponent } from '@shared/dialogs/dialog-yes-no/dialog-yes-no.component';
import { Document, Site, User } from '@shared/models';
import { SiteDocusignEnvelope } from '@shared/models/site-docusign-envelope.model';
import { ToastLevel } from '@shared/models/toast-message.module';
import { ToastService } from '@shared/services/toast.service';
import { SpinnerService } from '@shared/spinner/spinner.service';
import { UserActivityEventEnum } from '@shared/constants/user_activity.constants';
import { UserService } from '@core/api/user.service';
import { MatSort } from '@angular/material/sort';

@Component({
  selector: 'fc-site-doc-list',
  templateUrl: './site-document-list.component.html',
  styleUrls: ['./site-document-list.component.scss'],
  standalone: false
})
export class SiteDocumentListComponent implements OnInit, OnChanges, AfterViewInit {
  @ViewChild('documentUpload', { static: false }) documentUpload: ElementRef;
  @ViewChild(MatSort) sort: MatSort;
  @Input() activeSite: Site = null;
  @Input() isAdmin: boolean = false;

  destroyRef = inject(DestroyRef);

  existingDocs: Document[] = [];
  dataSource = new MatTableDataSource<Document>([]);
  envelopes: SiteDocusignEnvelope[] = [];
  displayedColumns: string[] = ['title', 'type', 'actions'];
  envelopeColumns: string[] = ['sentDate', 'description', 'recipient', 'status'];
  documentTypeOptions: string[] = [];
  uploadedByOptions: string[] = [];
  documentFilters = {
    title: '',
    type: [] as string[],
    uploadedBy: [] as string[],
  };

  defaultFileTypes: string = '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg';
  actAsUser: User = null;
  isReadAdmin: boolean = false;

  constructor(
    readonly store: Store<fromApp.AppState>,
    readonly formBuilder: UntypedFormBuilder,
    readonly documentService: DocumentService,
    readonly userService: UserService,
    readonly spinnerService: SpinnerService,
    public dialog: MatDialog,
    readonly toastService: ToastService
  ) {
    let lastFilter = '';
    let parsedFilters: typeof this.documentFilters = this.documentFilters;
    this.dataSource.filterPredicate = (data: Document, filter: string) => {
      if (filter !== lastFilter) {
        lastFilter = filter;
        parsedFilters = filter ? JSON.parse(filter) : this.documentFilters;
      }
      return this.matchesDocumentFilter(data, parsedFilters);
    };
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.activeSite?.currentValue) {
      this.envelopes = this.activeSite.siteDocusignEnvelopes.map(d => cloneDeep(d)).sort((a, b) => b.id - a.id);
      this.existingDocs = this.activeSite.documents
        .filter(d => this.isAdmin ? true : !d.documentType.isPrivate)
        .map(d => cloneDeep(d))
        .sort((a, b) => b.id - a.id);

      this.uploadedByOptions = [...new Set(this.existingDocs
        .map(document => document.user?.getDisplayName()))].sort();
      this.dataSource.data = this.existingDocs;
      this.applyFilters();
      if (this.isAdmin && !this.envelopeColumns.includes('actions')) {
        this.envelopeColumns.push("actions");
      }
      if (this.isAdmin || this.activeSite.submittedDate) {
        this.displayedColumns.unshift('added', 'uploadedBy');
        this.documentTypeOptions = [...new Set(this.existingDocs
          .map(document => document.documentType?.name))].sort();
      }
    }
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
    this.dataSource.sortingDataAccessor = (item: Document, column: string) => {
      switch (column) {
        case 'added':
          return item.createDate ? new Date(item.createDate).getTime() : 0;
        case 'title':
          return item.name?.toLowerCase() || '';
        case 'type':
          return item.documentType?.name?.toLowerCase() || '';
        case 'uploadedBy':
          return item.user.getDisplayName().toLowerCase() || '';
        default:
          return '';
      }
    };
  }

  ngOnInit() {
    this.store.select(selectActAsUser)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(actAsUser => {
        this.isReadAdmin = actAsUser?.isReadOnlyAdmin ?? false;
      });
  }

  downloadDocument(doc: Document, open: boolean): void {
    this.documentService
      .downloadDocument(this.activeSite.identifier, doc.identifier, doc.name, open)
      .then(uri => window.open(uri, '_blank'));
  }

  deleteDocument(doc: Document): void {
    this.dialog
      .open(DialogYesNoComponent, {
        panelClass: 'fc-yes-no-dialog',
        data: {
          title: 'Delete Confirmation',
          line1: 'Are you sure you wish to permanently delete this document?',
          buttonTextYes: 'Delete',
          buttonTextNo: 'Cancel',
        },
      })
      .afterClosed()
      .subscribe(result => {
        if (result) {
          this.documentService.deleteDocument(this.activeSite.identifier, doc.identifier).subscribe(() => {
            this.userService.logUserActivity({
              event: UserActivityEventEnum.SITE_MODIFICATION,
              details: `Document ${doc.name} deleted`,
              siteId: this.activeSite.id
            });
            this.toastService.sendMessage(`Document Deleted Successfully`, ToastLevel.SUCCESS);
            this.store.dispatch(setActiveSite({ id: this.activeSite.id }));
          })
        }
      });
  }

  editDocument(doc: Document) {
    this.dialog
      .open(DialogUploadDocComponent, {
        data: {
          filename: doc.name,
          description: doc.description,
          docTypeId: doc.documentTypeId,
          comments: doc.comments,
          isAdmin: this.isAdmin
        },
      })
      .afterClosed()
      .subscribe(result => {
        if (result) {
          const docUpdate = {
            document_type_id: result.docTypeId,
            name: doc.name,
            description: result.description,
            identifier: doc.identifier,
          };
          this.documentService.updateDocument(docUpdate).subscribe(() => {
            this.userService.logUserActivity({
              event: UserActivityEventEnum.SITE_MODIFICATION,
              details: `Document ${doc.identifier} updated`,
              siteId: this.activeSite.id
            });
            this.toastService.sendMessage(`Document Updated Successfully`, ToastLevel.SUCCESS);
            this.store.dispatch(setActiveSite({ id: this.activeSite.id }));
          })
        }
      });
  }

  uploadDocument(file: File, docTypeId: number, description: string, comments?: string): void {
    const newDoc = {
      site_id: this.activeSite.id,
      document_type_id: docTypeId,
      name: file.name,
      description: description,
      comments: comments,
    };
    this.documentService.uploadAndCreateDocument(
      this.activeSite,
      file,
      newDoc,
      true
    );
  }

  selectDocument(): void {
    const documentUpload = this.documentUpload.nativeElement;
    documentUpload.onchange = () => {
      if (documentUpload.files.length === 1) {
        const file = documentUpload.files[0];
        if (!file.name.startsWith('.') && this.checkFileType(file.name)) {
          this.dialog
            .open(DialogUploadDocComponent, {
              data: {
                filename: file.name,
                isAdmin: this.isAdmin
              },
            })
            .afterClosed()
            .subscribe(result => {
              if (result) {
                this.uploadDocument(file, result.docTypeId, result.description, result.comments);
              }
              this.documentUpload.nativeElement.value = '';
            });
        } else {
          this.documentUpload.nativeElement.value = '';
        }
      }
    };
    documentUpload.click();
  }

  checkFileType(fileName: string, fileTypes: string[] = this.defaultFileTypes.split(','), showToast: boolean = true) {
    let fileType = fileName.split('.').pop();
    if (fileTypes.includes('.' + fileType.toLowerCase())) {
      return true;
    } else if (showToast) {
      this.toastService.sendMessage(`Unsupported file type: ${fileType}.`, ToastLevel.WARN);
    }
    return false;
  }

  viewEnvelope(element: SiteDocusignEnvelope) {
    let url = `https://apps-d.docusign.com/send/documents/details/${element.envelopeId}`;
    if (environment.env == 'prod') {
      url = `https://apps.docusign.com/send/documents/details/${element.envelopeId}`;
    }
    window.open(url, '_blank');
  }

  voidEnvelope(element: SiteDocusignEnvelope) {
    this.dialog
      .open(DialogYesNoComponent, {
        panelClass: 'fc-yes-no-dialog',
        data: {
          title: 'Void Confirmation',
          line1: 'Are you sure you wish to void this envelope?',
          buttonTextYes: 'Void',
          buttonTextNo: 'Cancel',
        },
      })
      .afterClosed()
      .subscribe(result => {
        if (result) {
          this.spinnerService.show('app-spinner', 'Voiding Envelope');
          this.documentService.voidEnvelope(element.envelopeId).subscribe(result => {
            this.spinnerService.hide('app-spinner');
            if (result) {
              this.toastService.sendMessage(`Envelope voided successfully.`, ToastLevel.SUCCESS);
              this.store.dispatch(setActiveSite({ id: this.activeSite.id }));
            }
          });
        }
      });
  }

  resendEnvelope(element: SiteDocusignEnvelope) {
    this.spinnerService.show('app-spinner', 'Resending Envelope');
    this.documentService.resendEnvelope(element.envelopeId).subscribe(result => {
      this.spinnerService.hide('app-spinner');
      if (result) {
        this.toastService.sendMessage(`Envelope resent successfully.`, ToastLevel.SUCCESS);
        this.store.dispatch(setActiveSite({ id: this.activeSite.id }));
      }
    });
  }

  applyFilters() {
    this.dataSource.filter = JSON.stringify(this.documentFilters);
  }

  private matchesDocumentFilter(data: Document, filters: typeof this.documentFilters): boolean {
    const titleFilter = filters.title.trim().toLowerCase();
    const typeFilter = filters.type;
    const uploadedByFilter = filters.uploadedBy;

    const titleValue = [data.name || '', data.description || ''].join(' ').toLowerCase();
    const typeValue = data.documentType?.name || '';
    const uploadedByValue = data.user.getDisplayName();

    return (!titleFilter || titleValue.includes(titleFilter))
      && (!uploadedByFilter.length || uploadedByFilter.includes(uploadedByValue))
      && (!typeFilter.length || typeFilter.includes(typeValue));
  }

  sortIcon(column: string): string[] {
    if (this.sort?.active !== column || this.sort.direction === '') {
      return ['fal', 'sort'];
    }
    return ['fas', this.sort.direction === 'asc' ? 'sort-up' : 'sort-down'];
  }
}
